import React from "react";
import { Link } from "react-router-dom";
import { Home, Layers3, Newspaper } from "lucide-react";
import { Header } from "../components/site/Header";
import { Footer } from "../components/site/Footer";
import { SEO } from "../components/site/SEO";
import { useI18n } from "../i18n";

/**
 * Visible 404 page for client-side (SPA) navigation to unknown paths.
 * Direct server hits on unknown URLs are answered with a real HTTP 404 plus
 * the static public/404.html error document by the hosting/middleware layer.
 */
export default function NotFound() {
  const { lang } = useI18n();
  const de = lang !== "en";
  return (
    <div data-testid="notfound-page" className="min-h-screen bg-[#030712] text-white flex flex-col">
      <SEO
        path="/404"
        title={de ? "Seite nicht gefunden | NexTech.Law" : "Page not found | NexTech.Law"}
        description={
          de
            ? "Die angeforderte Seite wurde nicht gefunden."
            : "The requested page could not be found."
        }
        noindex
      />
      <Header />
      <main className="flex-1 flex items-center">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-32">
          <div className="font-mono-tech text-xs uppercase tracking-[0.26em] text-[#3B82F6]">
            404
          </div>
          <h1 data-testid="notfound-title" className="mt-5 font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-white">
            {de ? "Diese Seite wurde nicht gefunden." : "This page could not be found."}
          </h1>
          <p className="mt-5 text-lg leading-relaxed text-gray-300">
            {de
              ? "Die angeforderte Adresse existiert nicht oder wurde verschoben. Über die folgenden Links gelangen Sie zurück zu den wichtigsten Bereichen."
              : "The address you requested does not exist or has moved. Use the links below to return to the main sections."}
          </p>
          <div className="mt-9 flex flex-col sm:flex-row flex-wrap gap-3">
            <Link
              to="/"
              data-testid="notfound-home-link"
              className="inline-flex min-h-11 items-center justify-center gap-2 bg-[#0052FF] hover:bg-[#2563EB] text-white text-sm font-semibold px-5 py-3 rounded-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:ring-offset-2 focus:ring-offset-[#030712]"
            >
              <Home size={15} /> {de ? "Zur Startseite" : "Back to homepage"}
            </Link>
            <Link
              to="/services"
              data-testid="notfound-services-link"
              className="inline-flex min-h-11 items-center justify-center gap-2 border border-white/15 hover:border-[#3B82F6]/60 hover:bg-[#0052FF]/10 text-white text-sm font-semibold px-5 py-3 rounded-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:ring-offset-2 focus:ring-offset-[#030712]"
            >
              <Layers3 size={15} /> {de ? "Leistungen ansehen" : "View services"}
            </Link>
            <Link
              to="/insights"
              data-testid="notfound-insights-link"
              className="inline-flex min-h-11 items-center justify-center gap-2 border border-white/15 hover:border-[#3B82F6]/60 hover:bg-[#0052FF]/10 text-white text-sm font-semibold px-5 py-3 rounded-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:ring-offset-2 focus:ring-offset-[#030712]"
            >
              <Newspaper size={15} /> Insights
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
